import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Table as AntTable } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Settings } from 'lucide-react';
import Table from '../../components/Table';
import TableHeader from '../../components/TableHeader';
import ExpandIcons from '../../components/ExpandIcons';
import ExpandRow from '../../components/ExpandRow';
import icons from '../../../main/utils/icons';
import ProjectsTable from '../Tables/ProjectsTable';
import ModalUpdateAdminAndLecturer from '../ModalUser/ModalUpdateAdminAndLecturer';
import {
  hasAccessSettingsStudent,
  hasAccessUndeleteUser,
} from '../../utils/access';
import ROLES from '../../../main/utils/roles';
import pageNames from '../../utils/page_names';
import Button from '../../../main/components/Button';

const columns = ({ role, visibleColumnDaysRemaining = false, locales }) => {
  const columnsArray = [
    {
      title: locales.admin.fullName,
      dataIndex: 'fullName',
      key: 'fullName',
      render: (fullName) => {
        const displayName =
          fullName && fullName.length > 200
            ? `${fullName.substring(0, 200)}...`
            : fullName || '';
        return (
          <p
            title={fullName || ''}
            className="overflow-hidden text-wrap"
            style={{ maxWidth: '350px' }}
          >
            {displayName}
          </p>
        );
      },
    },
    {
      title: locales.admin.contact,
      dataIndex: 'email',
      key: 'email',
      render: (_, row) => {
        return (
          <div>
            <p
              title={row.phone || ''}
              className="overflow-hidden text-wrap"
              style={{ maxWidth: '200px' }}
            >
              {row.phone && row.phone.length > 200
                ? `${row.phone.substring(0, 200)}...`
                : row.phone || ''}
            </p>
            <p
              title={row.email || ''}
              className="overflow-hidden text-wrap"
              style={{ maxWidth: '350px' }}
            >
              {row.email && row.email.length > 200
                ? `${row.email.substring(0, 200)}...`
                : row.email || ''}
            </p>
          </div>
        );
      },
    },
    {
      title: locales.admin.project,
      dataIndex: 'bmcsCount',
      key: 'bmcsCount',
      render: (_, row) => {
        if (row.deletedAt != null) {
          return <div />;
        }

        return row.bmcsCount || 0;
      },
    },
    {
      title: locales.admin.activityTotal,
      dataIndex: 'sumTimeTracks',
      key: 'sumTimeTracks',
      render: (_, row) => {
        if (row.deletedAt != null) {
          return <div />;
        }

        return row.sumTimeTracks || 0;
      },
    },
    {
      title: locales.admin.last30DaysMin,
      dataIndex: 'sumLastTimeTracks',
      key: 'sumLastTimeTracks',
      render: (_, row) => {
        if (row.deletedAt != null) {
          return <div />;
        }

        return row.sumLastTimeTracks || 0;
      },
    },
    {
      title: locales.admin.updated,
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt, row) => {
        if (row.deletedAt != null) {
          return <div />;
        }

        moment.locale('en');
        return moment(new Date(updatedAt)).format('DD MMM YYYY');
      },
    },
    AntTable.EXPAND_COLUMN,
  ];

  const { length } = columnsArray;
  if (role === ROLES.SUPER_ADMIN) {
    if (visibleColumnDaysRemaining) {
      columnsArray.splice(length - 2, 0, {
        title: locales.admin.daysRemaining,
        dataIndex: 'daysRemaining',
        key: 'daysRemaining',
        render: (daysRemaining, row) => {
          if (row.deletedAt != null) {
            return <div />;
          }
          return daysRemaining === null ? '-' : daysRemaining;
        },
      });
    }

    columnsArray.splice(length - 1, 0, {
      title: locales.admin.deleted,
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      render: (deletedAt) => {
        if (deletedAt == null) {
          return <span>{locales.admin.active}</span>;
        }
        moment.locale('en');
        return moment(new Date(deletedAt)).format('DD MMM YYYY');
      },
    });
  }
  return columnsArray;
};

const StudentLists = ({
  byId,
  by,
  establishmentId,
  locales,
  currentPage = null,
}) => {
  const [search, setSearch] = useState(undefined);
  const [showSoftDeletedUsers, setShowSoftDeletedUsers] = useState(false);

  const [
    visibleModalUpdateAdminAndLecturer,
    setVisibleModalUpdateAdminAndLecturer,
  ] = useState(false);
  const [updateUserId, setUpdateUserId] = useState(null);

  const {
    students: { data, meta, loading },
    pagination: { pageSize: initPageSize },
    user,
  } = useSelector((state) => state);

  const {
    students: {
      fetchStudents,
      sortStudents,
      reload,
      toggleShowSoftDeletedUsers,
    },
  } = useDispatch();

  useEffect(() => {
    fetchStudents({
      byId,
      by,
      search,
      limit: initPageSize,
      establishmentId,
      showSoftDeletedUsers,
    });
  }, [byId, by, search, initPageSize, establishmentId, showSoftDeletedUsers]);

  const handleCloseModalUpdateAdminAndLecturer = () => {
    setVisibleModalUpdateAdminAndLecturer(false);
    setUpdateUserId(null);
  };

  const handleOpenModalUpdateAdminAndLecturer = (userId) => {
    setVisibleModalUpdateAdminAndLecturer(true);
    setUpdateUserId(userId);
  };

  const showSoftDeletedUsersCheckBox = [
    <div
      className="flex flex-row gap-2 items-center mr-10"
      key="showSoftDeletedUsers"
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label htmlFor="showSoftDeletedUsers" className="w-max">
        {locales.admin.showSoftDeletedUsers}
      </label>
      <br />
      <input
        type="checkbox"
        id="showSoftDeletedUsers"
        name="showSoftDeletedUsers"
        value="showSoftDeletedUsers"
        checked={showSoftDeletedUsers}
        className="w-4 h-4 text-blue-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-400 focus:ring-2"
        onChange={() => {
          toggleShowSoftDeletedUsers(!showSoftDeletedUsers);
          setShowSoftDeletedUsers(!showSoftDeletedUsers);
        }}
      />
    </div>,
  ];

  return (
    <>
      <ModalUpdateAdminAndLecturer
        visible={visibleModalUpdateAdminAndLecturer}
        onClose={handleCloseModalUpdateAdminAndLecturer}
        userId={updateUserId}
        param={{ byId, by, search, limit: initPageSize }}
        locales={locales}
        reload={reload}
        showSoftDeletedUsers
      />
      <TableHeader
        title={locales.admin.students}
        count={loading ? <Spin /> : meta?.count || 0}
        onSearch={(searchValue) => {
          setSearch(searchValue);
        }}
        searchLoading={loading}
        settings={
          !hasAccessUndeleteUser(user) ? [] : showSoftDeletedUsersCheckBox
        }
      />
      <Table
        columns={columns({
          role: user?.current?.role,
          visibleColumnDaysRemaining: currentPage === pageNames.superAdmin,
          locales,
        })}
        dataSource={data}
        loading={loading}
        total={meta?.count || 0}
        current={meta?.page || 1}
        onChange={(pagination) => {
          const { current, pageSize } = pagination;
          fetchStudents({
            byId,
            by,
            establishmentId,
            search,
            page: current,
            limit: pageSize,
            showSoftDeletedUsers,
          });
        }}
        expandRowByClick={(record) => record.deletedAt == null}
        rowClassName={(record) =>
          `ant-table-row admin-table__row ant-table-clickable ${
            record.deletedAt != null && 'deactivated'
          }`
        }
        expandable={{
          expandedRowRender: (record) => {
            return (
              <ExpandRow>
                <ProjectsTable byId={record.id} />
              </ExpandRow>
            );
          },
          rowExpandable: (record) =>
            record.deletedAt == null && record.bmcsCount > 0,
          expandIcon: ({ expanded, onExpand, record }) => {
            return expanded ? (
              <ExpandIcons>
                {hasAccessSettingsStudent(user) && (
                  <Button
                    onClick={() => {
                      handleOpenModalUpdateAdminAndLecturer(record.id);
                    }}
                    variant="outline"
                    size="wrapped"
                    className="p-2"
                  >
                    <Settings
                      className={
                        record.deletedAt != null ? 'stroke-[#FF8C00]' : ''
                      }
                    />
                  </Button>
                )}
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => onExpand(record, e)}
                >
                  {icons.rowExpandIconClose}
                </div>
              </ExpandIcons>
            ) : (
              <ExpandIcons>
                {hasAccessSettingsStudent(user) && (
                  <Button
                    onClick={() => {
                      handleOpenModalUpdateAdminAndLecturer(record.id);
                    }}
                    variant="outline"
                    size="wrapped"
                    className="p-2"
                  >
                    <Settings
                      className={
                        record.deletedAt != null ? 'stroke-[#FF8C00]' : ''
                      }
                    />
                  </Button>
                )}
                {record.deletedAt == null && record.bmcsCount > 0 && (
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => onExpand(record, e)}
                  >
                    {icons.rowExpandIcon}
                  </div>
                )}
              </ExpandIcons>
            );
          },
        }}
        meta={meta}
        onSort={(params) => {
          sortStudents({
            ...params,
            showSoftDeletedUsers,
          });
        }}
      />
    </>
  );
};

StudentLists.propTypes = {
  byId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  by: PropTypes.oneOf(['users', 'classrooms', 'establishments']),
};
export default StudentLists;
